import React, { useState } from 'react';
import { LogIn, UserPlus, Eye, EyeOff, Loader2 } from 'lucide-react';
import dulusBannerWhite from '../assets/images/dulus-banner-white.png';
import dulusBannerDark from '../assets/images/dulus-banner-dark.png';

const LoginScreen = ({ onLogin, onSignup, darkMode, sessionMsg, onClearSessionMsg }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validatePassword = (pwd) => {
    const passwordRegex = /^[a-zA-Z0-9@#-]+$/;
    return passwordRegex.test(pwd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validar caracteres de password en signup
    if (!isLogin && !validatePassword(password)) {
      setError('La contraseña solo puede contener letras, números y los caracteres @, -, #');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await onLogin(email, password);
      } else {
        await onSignup(email, password, name, invitationToken);
      }
    } catch (err) {
      setError(err.message || 'Error de autenticación');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const bgClass = darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100';
  const cardClass = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const inputClass = darkMode
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-gray-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-500';
  const textClass = darkMode ? 'text-white' : 'text-gray-900';
  const mutedClass = darkMode ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bgClass}`}>
      <div className={`w-full max-w-md p-8 rounded-2xl shadow-xl border-2 ${cardClass}`}>
        {/* Logo/Title */}
        <div className="text-center mb-6">
          <img src={darkMode ? dulusBannerWhite : dulusBannerDark} alt="Dulus" className="w-52 h-auto mx-auto" style={{ transform: 'translateX(10%)' }} />
          <br/>
          <hr/>
          <p className={`text-x mt-2 ${sessionMsg ? (darkMode ? 'text-yellow-400' : 'text-yellow-600') : mutedClass}`}>{sessionMsg || 'loans tracker app'}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 border rounded-xl text-sm ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <div>
              <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                Nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-500 transition-all ${inputClass}`}
                required={!isLogin}
              />
            </div>
          )}

          <div>
            <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
              Correo electrónico
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={onClearSessionMsg}
              placeholder="correo@ejemplo.com"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-500 transition-all ${inputClass}`}
              required
            />
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
              Contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={onClearSessionMsg}
                placeholder="••••••••"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-500 transition-all pr-12 ${inputClass}`}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 ${mutedClass} hover:${textClass}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div>
              <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                Token de invitación
              </label>
              <input
                type="text"
                value={invitationToken}
                onChange={(e) => setInvitationToken(e.target.value.toUpperCase())}
                placeholder="Pedir al +1 829 872-4331"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-500 transition-all font-mono tracking-widest text-center uppercase ${inputClass}`}
                required={!isLogin}
                maxLength={8}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-xl font-bold shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-800 text-white hover:bg-gray-900'}`}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isLogin ? (
              <>
                <LogIn className="w-5 h-5" />
                Iniciar sesión
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Crear cuenta
              </>
            )}
          </button>
        </form>

        {/* Toggle Login/Signup */}
        <div className="mt-6 text-center">
          <p className={mutedClass}>
            {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
              }}
              className={`ml-2 font-bold transition-colors ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-600'}`}
            >
              {isLogin ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
